// Vendor → Ödeme Yöntemleri (HENT, müşteri firmalara tahsilat için sunduğu kanallar)
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const TYPE_LABEL = {
  bank_transfer: 'Havale / EFT',
  credit_card:   'Kredi Kartı',
  other:         'Diğer',
};
const TYPE_ICON = { bank_transfer: '🏦', credit_card: '💳', other: '💰' };
const CURRENCIES = ['TRY','USD','EUR','GBP'];
const PROVIDERS = [
  { code: '',       label: '— Yok —' },
  { code: 'iyzico', label: 'Iyzico' },
  { code: 'paytr',  label: 'PayTR' },
  { code: 'stripe', label: 'Stripe' },
  { code: 'manual', label: 'Manuel' },
];

export default function VendorPaymentMethodsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor/payment-methods');
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (m) => {
    try {
      await api.put('/vendor/payment-methods/' + m.id, { is_enabled: !m.is_enabled });
      load();
    } catch (e) { alert(e?.response?.data?.message || 'Hata'); }
  };

  const remove = async (m) => {
    if (!window.confirm(`"${m.name}" ödeme yöntemi silinsin mi?`)) return;
    try {
      await api.delete('/vendor/payment-methods/' + m.id);
      load();
    } catch (e) { alert(e?.response?.data?.message || 'Hata'); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Ödeme Yöntemleri"
        unit="yöntem"
        count={list.length}
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}>+ Yeni Yöntem</Button>}
        onRefresh={load} refreshing={loading}
      />
      <div className="stocks-table-wrap">
        {loading ? <div style={{ padding: 40 }}>Yükleniyor...</div>
         : list.length === 0 ? <EmptyState title="Henüz ödeme yöntemi yok"
                                            description="Müşterilerin aboneliklerini ödeyebilmesi için en az bir yöntem ekleyin (örn. Garanti BBVA IBAN)." />
         : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th style={{ width: 60, textAlign:'center' }}>Aktif</th>
                <th>Ad / Açıklama</th>
                <th style={{ width: 150 }}>Tip</th>
                <th>IBAN / Hesap</th>
                <th style={{ width: 80 }}>Para</th>
                <th style={{ width: 80, textAlign:'center' }}>Sıra</th>
                <th style={{ width: 180 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id}>
                  <td style={{ textAlign:'center' }}>
                    <input type="checkbox" checked={!!m.is_enabled} onChange={() => toggleEnabled(m)} />
                  </td>
                  <td>
                    <strong>{m.name}</strong>
                    {m.description && <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)', marginTop: 2 }}>{m.description}</div>}
                  </td>
                  <td>
                    <span style={{ fontSize:'0.78rem' }}>{TYPE_ICON[m.type]} {TYPE_LABEL[m.type] || m.type}</span>
                    {m.provider && <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{m.provider}</div>}
                  </td>
                  <td style={{ fontFamily:'monospace', fontSize:'0.82rem' }}>
                    {m.iban && <div>{formatIban(m.iban)}</div>}
                    {m.bank_name && <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{m.bank_name}{m.account_name ? ' · ' + m.account_name : ''}</div>}
                  </td>
                  <td style={{ fontWeight: 600 }}>{m.currency}</td>
                  <td style={{ textAlign:'center' }}>{m.display_order}</td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setEdit(m)}>Düzenle</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(m)} style={{ color:'var(--danger)' }}>Sil</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(createOpen || edit) && (
        <PaymentMethodModal method={edit}
                            onClose={() => { setCreateOpen(false); setEdit(null); }}
                            onSaved={() => { setCreateOpen(false); setEdit(null); load(); }} />
      )}
    </div>
  );
}

function formatIban(iban) {
  if (!iban) return '';
  return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

function PaymentMethodModal({ method, onClose, onSaved }) {
  const editing = !!method;
  const [form, setForm] = useState({
    name:          method?.name          || '',
    type:          method?.type          || 'bank_transfer',
    is_enabled:    method?.is_enabled ?? true,
    display_order: method?.display_order || 0,
    bank_name:     method?.bank_name     || '',
    account_name:  method?.account_name  || '',
    iban:          method?.iban          || '',
    branch_code:   method?.branch_code   || '',
    account_no:    method?.account_no    || '',
    currency:      method?.currency      || 'TRY',
    provider:      method?.provider      || '',
    description:   method?.description   || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!form.name.trim()) return setErr('Ad zorunlu');
    setBusy(true);
    try {
      const payload = {
        ...form,
        iban: form.iban ? form.iban.replace(/\s/g, '').toUpperCase() : null,
      };
      if (editing) await api.put('/vendor/payment-methods/' + method.id, payload);
      else         await api.post('/vendor/payment-methods', payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Hata');
      setBusy(false);
    }
  };

  const isBank = form.type === 'bank_transfer';

  return (
    <Modal title={editing ? 'Ödeme Yöntemi Düzenle' : 'Yeni Ödeme Yöntemi'} onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12, maxWidth: 600 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Tip" required>
            <select className="ui-input" value={form.type} onChange={e => f('type', e.target.value)}>
              <option value="bank_transfer">{TYPE_ICON.bank_transfer} Havale / EFT</option>
              <option value="credit_card">{TYPE_ICON.credit_card} Kredi Kartı</option>
              <option value="other">{TYPE_ICON.other} Diğer</option>
            </select>
          </FormField>
          <FormField label="Para Birimi">
            <select className="ui-input" value={form.currency} onChange={e => f('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormField>
        </div>

        <FormField label="Ad (Görünür Etiket)" required>
          <input className="ui-input" value={form.name} onChange={e => f('name', e.target.value)}
                 placeholder={isBank ? 'Örn: Garanti BBVA - HENT TL Hesabı' : 'Örn: Iyzico Kredi Kartı'} autoFocus />
        </FormField>

        {isBank && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
              <FormField label="Banka">
                <input className="ui-input" value={form.bank_name} onChange={e => f('bank_name', e.target.value)} placeholder="Garanti BBVA" />
              </FormField>
              <FormField label="Hesap Sahibi">
                <input className="ui-input" value={form.account_name} onChange={e => f('account_name', e.target.value)} placeholder="HENT Yazılım A.Ş." />
              </FormField>
            </div>
            <FormField label="IBAN" required={isBank}>
              <input className="ui-input" value={form.iban} onChange={e => f('iban', e.target.value)}
                     placeholder="TR12 3456 7890 1234 5678 9012 34" style={{ fontFamily:'monospace' }} maxLength={34} />
            </FormField>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
              <FormField label="Şube Kodu">
                <input className="ui-input" value={form.branch_code} onChange={e => f('branch_code', e.target.value)} />
              </FormField>
              <FormField label="Hesap No">
                <input className="ui-input" value={form.account_no} onChange={e => f('account_no', e.target.value)} />
              </FormField>
            </div>
          </>
        )}

        {form.type === 'credit_card' && (
          <FormField label="Sağlayıcı">
            <select className="ui-input" value={form.provider} onChange={e => f('provider', e.target.value)}>
              {PROVIDERS.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
            </select>
          </FormField>
        )}

        <FormField label="Açıklama (müşteriye gösterilir)">
          <textarea className="ui-input" rows={2} value={form.description} onChange={e => f('description', e.target.value)}
                    placeholder={isBank ? 'Ödemenizi yaptıktan sonra dekontu bize ileterek bilgilendirin.' : ''} />
        </FormField>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Sıralama (küçük üstte)">
            <input className="ui-input" type="number" value={form.display_order}
                   onChange={e => f('display_order', parseInt(e.target.value) || 0)} />
          </FormField>
          <FormField label="Durum">
            <label style={{ display:'inline-flex', alignItems:'center', gap: 8, padding:'8px 0', fontSize: 14 }}>
              <input type="checkbox" checked={form.is_enabled} onChange={e => f('is_enabled', e.target.checked)} />
              Aktif (müşteriye göster)
            </label>
          </FormField>
        </div>

        {err && <div style={{ color:'var(--danger)', fontSize: 13 }}>{err}</div>}
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : (editing ? 'Kaydet' : 'Oluştur')}</Button>
        </div>
      </div>
    </Modal>
  );
}
